import { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Stack, Title } from '@mantine/core';

import { ProductEnum } from '@/api';
import { wentRight, wentWrong, askToConfirm } from '@/helpers/notification_helpers';

import { SubmissionState } from '../types';
import type { ProductType, VendorAvailabilityData, VendorAvailabilityListItem } from '../types';
import { PRODUCT_LABELS } from '../constants';
import { useAvailabilityState } from '../hooks/useAvailabilityState';
import { useSubmitAvailability } from '../hooks/useSubmitAvailability';
import { deriveSubmissionState } from '../helpers';

import AvailabilityCalendarGrid from './AvailabilityCalendar.calendar';
import QuickActions from './AvailabilityCalendar.quick-actions';
import MealPeriodsSelector from './AvailabilityCalendar.meal-periods';
import SubmitSection from './AvailabilityCalendar.submit';
import RestaurantHeader from './AvailabilityCalendar.restaurant-header';
import CalendarLegend from './AvailabilityCalendar.legend';

type AvailabilityCalendarContentProps = {
  data: VendorAvailabilityData;
  vendorId: string;
  productType: ProductType;
  month: number;
  year: number;
  restaurant: VendorAvailabilityListItem;
  refetch: () => void;
  targetMonth: string;
  refetchAvailabilityList: () => void;
};

const AvailabilityCalendarContent: React.FC<AvailabilityCalendarContentProps> = ({
  data,
  vendorId,
  productType,
  month,
  year,
  restaurant,
  refetch,
  targetMonth,
  refetchAvailabilityList,
}) => {
  const [submissionState, setSubmissionState] = useState<SubmissionState>(() =>
    deriveSubmissionState(data)
  );

  const {
    availableDates,
    selectedMealPeriods,
    acknowledgementChecked,
    isDirty,
    activeQuickAction,
    toggleDate,
    applyQuickAction,
    toggleMealPeriod,
    setAcknowledgement,
    reset,
    getDatesPayload,
    getMealPeriodsPayload,
  } = useAvailabilityState(data, month, year, productType);

  const { submit, loading: submitLoading } = useSubmitAvailability();

  const isPopup = productType === ProductEnum.POPUP;
  const isReadOnly = submissionState === SubmissionState.PAST_CUTOFF;
  const showsEditButton =
    submissionState === SubmissionState.SUBMITTED ||
    (!data.requiresSubmission && submissionState === SubmissionState.NOT_SUBMITTED);

  useEffect(() => {
    reset(data);
    setSubmissionState(deriveSubmissionState(data));
  }, [data, reset]);

  const handleSubmit = useCallback(async () => {
    const result = await submit({
      vendorId,
      productType,
      month,
      year,
      dates: getDatesPayload(),
      mealPeriods: isPopup ? getMealPeriodsPayload() : undefined,
      cancellationAcknowledged: acknowledgementChecked,
      submissionVersion: data.submission?.version,
    });

    if (result.success) {
      wentRight('No further action is required.', 'Availability Submitted');
      setSubmissionState(SubmissionState.SUBMITTED);
      refetch();
      refetchAvailabilityList();
    } else {
      wentWrong(result.errors.join(', '));
    }
  }, [
    submit,
    vendorId,
    productType,
    month,
    year,
    getDatesPayload,
    getMealPeriodsPayload,
    isPopup,
    acknowledgementChecked,
    data.submission?.version,
    refetch,
    refetchAvailabilityList,
  ]);

  const handleEdit = useCallback(() => {
    setSubmissionState(SubmissionState.EDITING);
  }, []);

  const handleCancel = useCallback(() => {
    if (!isDirty) {
      setSubmissionState(deriveSubmissionState(data));
      return;
    }
    askToConfirm({
      title: 'Discard changes?',
      message: `Are you sure you want to discard changes to this ${productType.toLowerCase()} availability?`,
      confirm: 'Discard changes',
      cancel: 'Cancel',
      onConfirm: () => {
        reset(data);
        setSubmissionState(deriveSubmissionState(data));
      },
    });
  }, [data, reset, isDirty, productType]);

  return (
    <Stack gap={0}>
      <RestaurantHeader restaurant={restaurant} data={data} />

      <Grid p="lg">
        <Grid.Col span={{ base: 12, sm: 6, md: 7 }} pr={{ base: 0, md: 'lg' }}>
          <Title order={3} mb="xs">
            {PRODUCT_LABELS[productType]} Availability
          </Title>
          <Stack gap="xs" w="fit-content">
            <CalendarLegend targetMonth={targetMonth} />
            <AvailabilityCalendarGrid
              month={month}
              year={year}
              availableDates={availableDates}
              onToggleDate={toggleDate}
              readOnly={isReadOnly || showsEditButton}
            />
          </Stack>
          {!isReadOnly && !showsEditButton && (
            <Box mt="md">
              <QuickActions
                onAction={applyQuickAction}
                disabled={false}
                activeAction={activeQuickAction}
              />
            </Box>
          )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 5 }}>
          <Stack gap="md">
            {isPopup && (
              <MealPeriodsSelector
                selectedPeriods={selectedMealPeriods}
                eligiblePeriods={data.eligibleMealPeriods}
                onToggle={toggleMealPeriod}
                disabled={isReadOnly || showsEditButton}
              />
            )}

            <SubmitSection
              submissionState={submissionState}
              submission={data.submission}
              acknowledgementChecked={acknowledgementChecked}
              onAcknowledgementChange={setAcknowledgement}
              onSubmit={handleSubmit}
              onEdit={handleEdit}
              onCancel={handleCancel}
              submitLoading={submitLoading}
              isDirty={isDirty}
              windowStatus={data.windowStatus}
              deadlineDate={data.deadlineDate}
              requiresSubmission={data.requiresSubmission}
              showsEditButton={showsEditButton}
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default AvailabilityCalendarContent;
